/**
 * The uuid predicates, in the one place both sides of the wire can read them.
 *
 * Every id column in the schema is a uuid, so "can this string name a row" is a
 * question the server asks when validating an argument and the client asks
 * before spending a request on one. Two copies of the pattern drift silently —
 * the failure is a value one side accepts and the other rejects, which surfaces
 * as a request that cannot succeed rather than as anything that fails a test.
 */
const HEX = '[0-9a-fA-F]';
const UUID_SHAPE = `${HEX}{8}-${HEX}{4}-${HEX}{4}-${HEX}{4}-${HEX}{12}`;
const SHORT_UUID_SHAPE = `${HEX}{8}`;
const UUID_REGEX = new RegExp(`^${UUID_SHAPE}$`, 'i');
const SHORT_UUID_REGEX = new RegExp(`^${SHORT_UUID_SHAPE}$`, 'i');

/** How many characters the short form of a uuid carries — its first group. */
export const SHORT_UUID_LENGTH = 8;

/**
 * JSON-Schema `pattern` accepting either form, for an argument a caller may
 * shorten. Both cases are spelled into the character class because JSON Schema
 * carries no case-insensitive flag, which is also why the shapes above are
 * written that way rather than leaning on their `i`.
 */
export const UUID_OR_SHORT_UUID_PATTERN = `^(${UUID_SHAPE}|${SHORT_UUID_SHAPE})$`;
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * A uuid or a short uuid found in free text, captured so `String.split` keeps
 * it. The full form comes first so the alternation consumes a whole uuid rather
 * than stopping at its first group. Neither form may sit against another letter
 * or digit — that is a longer token that merely starts like an id — and the
 * short form additionally stands down before a `-<hex>` continuation, which is
 * a malformed long id rather than an id of its own. A short run of eight digits
 * is excluded: in prose that is a date or an amount far more often than the
 * first group of a uuid.
 */
const UUID_IN_TEXT_REGEX = new RegExp(
  `(?<![0-9a-z])(${UUID_SHAPE}|(?![0-9]{8})${SHORT_UUID_SHAPE}(?!-[0-9a-f]))(?![0-9a-z])`,
  'i'
);

/** One run of text, flagged for whether it is an id. */
export interface IUuidTextSegment {
  text: string;
  isUuid: boolean;
}

/** Validates that a string is a well-formed UUID (any version, case-insensitive). */
export function isValidUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Validates the short form of a uuid — its first group, the eight characters
 * the UI truncates an id to. Unlike {@link splitOnUuids} this accepts an
 * all-digit value: a pasted short id is one the operator already read as an id,
 * so there is no prose to mistake it for.
 */
export function isShortUuid(value: string): boolean {
  return SHORT_UUID_REGEX.test(value);
}

/**
 * Splits `text` into alternating plain and id runs, left to right, so a caller
 * can render every id it finds as a link. Both the full form and the short form
 * count as ids. Concatenating the segments reproduces `text` exactly.
 */
export function splitOnUuids(text: string): IUuidTextSegment[] {
  // Map before filtering: `split` on a capturing pattern puts the ids at the odd
  // indices, and dropping the empty runs first would shift every one of them.
  return text
    .split(UUID_IN_TEXT_REGEX)
    .map((piece, index) => ({ text: piece, isUuid: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0);
}

/**
 * Strict v4 UUID predicate. Use this when validating capability tokens or
 * other security-critical inputs that we generate via `randomUUID()` (which
 * always emits v4) — narrowing the accepted shape removes the implicit
 * "any UUID matches" surface the more permissive `isValidUuid` would allow.
 */
export function isValidUuidV4(value: string): boolean {
  return UUID_V4_REGEX.test(value);
}
